import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle, SkipForward,
  FileText, Play, AlertTriangle, MapPin, Users, Calendar, Hash,
} from "lucide-react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Badge, { type BadgeVariant } from "../../common/Badge";
import { FormControl } from "../../common/FormControl";
import Skeleton from "../../common/Skeleton";
import Toast, { ToastMessage } from "../../common/Toast";
import sopInstanceService, {
  type ProcessInstanceDetail,
  type InstanceStep,
  type AuditExecution,
  ROLE_LABEL,
} from "../../../services/sopInstanceService";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedTextarea } from "../../../validation/ValidatedFields";

interface Props {
  instanceId: number;
  onClose: () => void;
  onRefreshParent: () => void;
  readOnly?: boolean;
}

// Map workflow statuses → shared Badge variants.
const execBadgeVariant = (s: string): BadgeVariant =>
  s === "COMPLETED" ? "success" : s === "IN_PROGRESS" ? "info" : "warning";

const stepBadgeVariant = (s: string): BadgeVariant =>
  s === "COMPLETED" ? "success" : s === "ACTIVE" ? "info" : s === "SKIPPED" ? "neutral" : "warning";

// ── Audit execution card ────────────────────────────────────────────────────

const AuditCard: React.FC<{
  exec: AuditExecution;
  canAct: boolean;
  onStart: () => Promise<void>;
  onComplete: () => Promise<void>;
}> = ({ exec, canAct, onStart, onComplete }) => {
  const [busy, setBusy] = useState(false);

  return (
    <div className="collapse-card">
      <div className="collapse-card__row">
        <span className="collapse-card__title" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <FileText size={12} className="text-blue-400 flex-shrink-0" />
          {exec.procedure_name}
        </span>
        <div className="collapse-card__meta">
          {canAct && exec.status === "PENDING" && (
            <Button
              variant="fill"
              icon={Play}
              disabled={busy}
              onClick={async () => { setBusy(true); try { await onStart(); } finally { setBusy(false); } }}
            >
              Start
            </Button>
          )}
          {canAct && exec.status === "IN_PROGRESS" && (
            <Button
              variant="update"
              icon={CheckCircle}
              disabled={busy}
              onClick={async () => { setBusy(true); try { await onComplete(); } finally { setBusy(false); } }}
            >
              Submit
            </Button>
          )}
        </div>
      </div>
      <div className="collapse-card__badges">
        <Badge variant={execBadgeVariant(exec.status)} size="sm">{exec.status}</Badge>
        {exec.procedure_type && <Badge variant="neutral" size="sm">{exec.procedure_type}</Badge>}
      </div>
    </div>
  );
};

// ── Single step row ──────────────────────────────────────────────────────────

// Flat table row — one SOP step per row (S.No · Step · Status · Action).
// The action controls (Mark Complete / Acknowledge) live inline; the notes
// field and the step's nested audit-procedures open in small popups instead of
// an inline expanding panel, so the modal reads as a clean checklist table
// (parity with the polished audit ComplianceWalkthrough).
const StepRow: React.FC<{
  step: InstanceStep;
  idx: number;
  myAuthorityIds: number[];
  onComplete: (stepId: number, notes: string) => Promise<void>;
  onSkip: (stepId: number) => Promise<void>;
  onExecRefresh: () => Promise<void>;
  readOnly?: boolean;
}> = ({ step, idx, myAuthorityIds, onComplete, onSkip, onExecRefresh, readOnly = false }) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const [procsOpen, setProcsOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const vh = useFieldValidation();
  const isMine = myAuthorityIds.includes(step.authority_id);
  const isActive = step.status === "ACTIVE";
  // History/archive view → nothing is actionable.
  const canAct = isMine && isActive && !readOnly;
  const execs = step.audit_executions;
  const execsPending = execs.some((e) => e.status !== "COMPLETED");
  const blockComplete = execsPending && execs.length > 0;

  return (
    <>
      <tr className="table-list-row">
        <td className="table-list-cell response-cell-sno">{idx + 1}</td>
        <td className="table-list-cell response-cell-question">
          <span className="table-list-cell-title">{step.step_name}</span>
          <span className="table-list-cell-sub">
            Step {step.step_number} · {step.authority_name} — <span className="italic">{ROLE_LABEL[step.role] ?? step.role}</span>
            {isMine && <span className="text-blue-500 font-semibold"> · You</span>}
          </span>
          {step.status === "COMPLETED" && step.notes && (
            <span className="table-list-cell-sub" style={{ fontStyle: "italic" }}>{step.notes}</span>
          )}
        </td>
        <td className="table-list-cell">
          <Badge variant={stepBadgeVariant(step.status)} size="sm">{step.status}</Badge>
        </td>
        <td className="table-list-cell table-list-cell--actions">
          <div className="response-action-cluster" style={{ flexWrap: "nowrap", justifyContent: "flex-end" }}>
            {execs.length > 0 && (
              <Button
                variant="secondary-outline"
                icon={FileText}
                data-testid={`BTN_step_${step.id}_procedures`}
                onClick={() => setProcsOpen(true)}
              >
                {execs.length} procedure{execs.length === 1 ? "" : "s"}{execsPending ? " · pending" : ""}
              </Button>
            )}
            {canAct && (
              <>
                <Button
                  variant="fill"
                  icon={CheckCircle}
                  disabled={busy || blockComplete}
                  title={blockComplete ? "Complete all audit procedures first" : undefined}
                  data-testid="BTN_mark_complete"
                  onClick={() => { setNotes(""); setNotesOpen(true); }}
                >
                  Mark Complete
                </Button>
                {step.role === "NOTIFY" && (
                  <Button
                    variant="outline"
                    icon={SkipForward}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true);
                      try { await onSkip(step.id); }
                      finally { setBusy(false); }
                    }}
                  >
                    Acknowledge
                  </Button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>

      {/* Notes popup — captures the optional step note before completing. */}
      {notesOpen && (
        <Modal isOpen onClose={() => setNotesOpen(false)} size="md">
          <Modal.Header onClose={() => setNotesOpen(false)}>Mark Complete — {step.step_name}</Modal.Header>
          <Modal.Body>
            <ValidatedTextarea
              vh={vh}
              name="notes"
              code={110}
              rows={4}
              noResize
              placeholder="Notes (optional)…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="INP_step_notes"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline" onClick={() => setNotesOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              variant="fill"
              icon={CheckCircle}
              disabled={busy || blockComplete}
              onClick={async () => {
                if (!vh.validateAll([{ name: "notes", code: 110, value: notes }])) return;
                setBusy(true);
                try { await onComplete(step.id, notes); setNotesOpen(false); }
                finally { setBusy(false); }
              }}
            >
              {busy ? "Saving…" : "Confirm"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Audit-procedures popup — nested executions with Start / Submit. */}
      {procsOpen && (
        <Modal isOpen onClose={() => setProcsOpen(false)} size="md">
          <Modal.Header onClose={() => setProcsOpen(false)}>Audit Procedures — {step.step_name}</Modal.Header>
          <Modal.Body>
            {blockComplete && (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2">
                <AlertTriangle size={11} />
                Complete all audit procedures before marking this step done.
              </div>
            )}
            {execs.map((ex) => (
              <AuditCard
                key={ex.id}
                exec={ex}
                canAct={canAct}
                onStart={async () => {
                  await sopInstanceService.startAuditExecution(ex.id);
                  await onExecRefresh();
                }}
                onComplete={async () => {
                  await sopInstanceService.completeAuditExecution(ex.id);
                  await onExecRefresh();
                }}
              />
            ))}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// ── Main modal ───────────────────────────────────────────────────────────────

const SopInstanceDetailModal: React.FC<Props> = ({ instanceId, onClose, onRefreshParent, readOnly = false }) => {
  const [detail, setDetail] = useState<ProcessInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [myAuthorityIds, setMyAuthorityIds] = useState<number[]>([]);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const reload = useCallback(async () => {
    try {
      const d = await sopInstanceService.getInstance(instanceId);
      setDetail(d);
    } catch {
      setToast({ type: "error", message: "Failed to load instance." });
    }
  }, [instanceId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await reload();
      setLoading(false);
    })();
  }, [reload]);

  // Resolve the authorities the logged-in person actually holds. A step is mine
  // when its authority_id is in this set (a person may hold several authorities,
  // so several steps can be mine). On failure, highlight nothing rather than
  // falsely claiming every active step.
  useEffect(() => {
    (async () => {
      try {
        setMyAuthorityIds(await sopInstanceService.getMyAuthorities());
      } catch {
        setMyAuthorityIds([]);
      }
    })();
  }, []);

  const handleComplete = async (stepId: number, notes: string) => {
    await sopInstanceService.completeStep(stepId, notes);
    await reload();
    onRefreshParent();
  };

  const handleSkip = async (stepId: number) => {
    await sopInstanceService.skipStep(stepId);
    await reload();
    onRefreshParent();
  };

  const handleExecRefresh = async () => {
    await reload();
  };

  if (!detail && !loading) return null;

  const progress = detail
    ? Math.round(((detail.completed_count ?? 0) / Math.max(detail.step_count ?? 1, 1)) * 100)
    : 0;

  return (
    <>
      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
      <Modal isOpen onClose={onClose} size="xl">
        <Modal.Header onClose={onClose}>
              SOP Instance - {detail?.process_name ?? "Loading…"}
        </Modal.Header>

        <Modal.Body>
          {loading ? (
            <div className="table-list response-qtable">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <colgroup>
                    <col className="response-col-sno" />
                    <col />
                    <col className="response-col-answer" />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="table-list-th response-th-sno">S.No</th>
                      <th className="table-list-th">Step</th>
                      <th className="table-list-th">Status</th>
                      <th className="table-list-th table-list-th--right">Action</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={true} aria-live="polite">
                    <Skeleton.TableRows cols={["2xs", "lg", "pill", { width: "icon", align: "right" }]} />
                  </tbody>
                </table>
              </div>
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Instance meta + progress on ONE line — meta chips left, the
                  progress bar fills the remaining width (no separate row / divider). */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="collapse-section__info-bar sop-info-bar" style={{ background: "transparent", margin: 0 }}>
                  {detail.transaction_type && (
                    <div className="collapse-section__info-item">
                      <Calendar size={12} className="text-amber-400" />
                      <span>{detail.transaction_type}</span>
                    </div>
                  )}
                  {detail.location_name && (
                    <div className="collapse-section__info-item">
                      <MapPin size={12} className="text-blue-400" />
                      <span>{detail.location_name}</span>
                    </div>
                  )}
                  {detail.population_name && (
                    <div className="collapse-section__info-item">
                      <Users size={12} className="text-purple-400" />
                      <span>{detail.population_name}</span>
                    </div>
                  )}
                  {detail.sample_text && (
                    <div className="collapse-section__info-item">
                      <Hash size={12} className="text-green-400" />
                      <span>{detail.sample_text}</span>
                    </div>
                  )}
                  {detail.initiated_by_name && (
                    <div className="collapse-section__info-item">
                      <span className="text-[11px]">by {detail.initiated_by_name}</span>
                    </div>
                  )}
                </div>
                <div className="step-progress-bar ml-auto">
                  <div className="step-progress-bar__track">
                    <div
                      className="step-progress-bar__fill bg-blue-400"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="step-progress-bar__text">
                    {detail.completed_count}/{detail.step_count} steps
                  </span>
                </div>
              </div>

              {/* Steps — flat checklist table (S.No · Step · Status · Action) */}
              <div className="table-list response-qtable sop-steps-table">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <colgroup>
                      <col className="response-col-sno" />
                      <col />
                      <col className="response-col-answer" />
                      <col />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="table-list-th response-th-sno">S.No</th>
                        <th className="table-list-th">Step</th>
                        <th className="table-list-th">Status</th>
                        <th className="table-list-th table-list-th--right">Action</th>
                      </tr>
                    </thead>
                    <tbody aria-live="polite">
                      {detail.steps.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="table-list-cell">
                            <p className="collapse-section__empty">No steps defined for this SOP.</p>
                          </td>
                        </tr>
                      ) : (
                        detail.steps.map((step, i) => (
                          <StepRow
                            key={step.id}
                            step={step}
                            idx={i}
                            myAuthorityIds={myAuthorityIds}
                            onComplete={handleComplete}
                            onSkip={handleSkip}
                            onExecRefresh={handleExecRefresh}
                            readOnly={readOnly}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Body>

        {!readOnly && detail && detail.status === "IN_PROGRESS" && (
          <Modal.Footer>
            <Button
              variant="inactive-outline"
              onClick={async () => {
                await sopInstanceService.cancelInstance(instanceId);
                setToast({ type: "success", message: "Instance cancelled." });
                onRefreshParent();
                onClose();
              }}
            >
              Cancel Instance
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default SopInstanceDetailModal;
